import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Modal, Alert, Pagination } from 'react-bootstrap';
import { FaSearch, FaEye, FaDownload, FaFilter, FaExclamationTriangle, FaInfo, FaCheck, FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const SuperAdminSystemLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [filters, setFilters] = useState({
        level: '',
        date_from: '',
        date_to: '',
        search: '',
    });
    const logsPerPage = 50;

    useEffect(() => {
        loadLogs();
    }, [currentPage, filters]);

    const loadLogs = async () => {
        try {
            const params = {
                page: currentPage,
                per_page: logsPerPage,
                ...filters,
            };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '') delete params[key];
            });

            const response = await api.get('/superadmin/logs/', { params });
            setLogs(response.data.logs || []);
            setTotalPages(response.data.total_pages || 1);
        } catch (error) {
            console.error('Error loading logs:', error);
            toast.error('Erreur lors du chargement des logs');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value,
        });
        setCurrentPage(1); // Reset to first page when filters change
    };

    const handleShowDetails = (log) => {
        setSelectedLog(log);
        setShowDetailModal(true);
    };

    const handleExport = async () => {
        try {
            const params = { ...filters };
            Object.keys(params).forEach(key => {
                if (params[key] === '') delete params[key];
            });

            const response = await api.get('/superadmin/logs/export/', {
                params,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `system_logs_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Logs exportés avec succès');
        } catch (error) {
            console.error('Error exporting logs:', error);
            toast.error('Erreur lors de l\'export');
        }
    };

    const getLogLevelIcon = (level) => {
        switch (level.toLowerCase()) {
            case 'error':
            case 'critical':
                return <FaTimes className="text-danger" />;
            case 'warning':
                return <FaExclamationTriangle className="text-warning" />;
            case 'info':
                return <FaInfo className="text-info" />;
            case 'debug':
                return <FaCheck className="text-secondary" />;
            default:
                return <FaInfo className="text-muted" />;
        }
    };

    const getLogLevelBadge = (level) => {
        const levelLower = level.toLowerCase();
        let variant = 'secondary';

        switch (levelLower) {
            case 'error':
            case 'critical':
                variant = 'danger';
                break;
            case 'warning':
                variant = 'warning';
                break;
            case 'info':
                variant = 'info';
                break;
            case 'debug':
                variant = 'light';
                break;
            default:
                variant = 'secondary';
        }

        return <Badge bg={variant}>{level}</Badge>;
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    const renderPagination = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        return (
            <Pagination className="justify-content-center">
                <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                />
                <Pagination.Prev
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                />
                {items}
                <Pagination.Next
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                />
                <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                />
            </Pagination>
        );
    };

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaSearch className="me-3" />
                        Logs Système
                    </h2>
                    <p className="text-muted">Consultez et analysez les logs de l'application</p>
                </Col>
                <Col className="text-end">
                    <Button variant="success" onClick={handleExport}>
                        <FaDownload className="me-2" />
                        Exporter
                    </Button>
                </Col>
            </Row>

            {/* Filtres */}
            <Card className="mb-4">
                <Card.Header>
                    <h5>
                        <FaFilter className="me-2" />
                        Filtres
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Niveau</Form.Label>
                                <Form.Select
                                    name="level"
                                    value={filters.level}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Tous les niveaux</option>
                                    <option value="DEBUG">Debug</option>
                                    <option value="INFO">Info</option>
                                    <option value="WARNING">Warning</option>
                                    <option value="ERROR">Error</option>
                                    <option value="CRITICAL">Critical</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Date début</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date_from"
                                    value={filters.date_from}
                                    onChange={handleFilterChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Date fin</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date_to"
                                    value={filters.date_to}
                                    onChange={handleFilterChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Recherche</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="search"
                                    placeholder="Message, utilisateur..."
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Table des logs */}
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>Logs système</h5>
                    <Badge bg="primary">{logs.length} logs affichés</Badge>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary"></div>
                            <p className="mt-3">Chargement des logs...</p>
                        </div>
                    ) : logs.length === 0 ? (
                        <Alert variant="info" className="text-center">
                            <FaInfo className="me-2" />
                            Aucun log trouvé avec les filtres actuels
                        </Alert>
                    ) : (
                        <>
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>Niveau</th>
                                        <th>Timestamp</th>
                                        <th>Utilisateur</th>
                                        <th>Module</th>
                                        <th>Message</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {getLogLevelIcon(log.level)}
                                                    <span className="ms-2">{getLogLevelBadge(log.level)}</span>
                                                </div>
                                            </td>
                                            <td className="text-nowrap">
                                                {formatTimestamp(log.timestamp)}
                                            </td>
                                            <td>{log.user || '-'}</td>
                                            <td>
                                                <Badge bg="light" text="dark">
                                                    {log.module || 'system'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div style={{
                                                    maxWidth: '300px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {log.message}
                                                </div>
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => handleShowDetails(log)}
                                                    title="Voir les détails"
                                                >
                                                    <FaEye />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {/* Pagination */}
                            {totalPages > 1 && renderPagination()}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal Détails du log */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Détails du log</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLog && (
                        <>
                            <Row>
                                <Col md={6}>
                                    <h6 className="text-muted">Informations générales</h6>
                                    <p><strong>Niveau :</strong> {getLogLevelBadge(selectedLog.level)}</p>
                                    <p><strong>Timestamp :</strong> {formatTimestamp(selectedLog.timestamp)}</p>
                                    <p><strong>Utilisateur :</strong> {selectedLog.user || 'Système'}</p>
                                    <p><strong>Module :</strong> {selectedLog.module || 'system'}</p>
                                    <p><strong>IP :</strong> {selectedLog.ip_address || 'N/A'}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-muted">Contexte</h6>
                                    <p><strong>User Agent :</strong> {selectedLog.user_agent || 'N/A'}</p>
                                    <p><strong>Session ID :</strong> {selectedLog.session_id || 'N/A'}</p>
                                    <p><strong>Request ID :</strong> {selectedLog.request_id || 'N/A'}</p>
                                    <p><strong>Endpoint :</strong> {selectedLog.endpoint || 'N/A'}</p>
                                    <p><strong>Méthode :</strong> {selectedLog.method || 'N/A'}</p>
                                </Col>
                            </Row>
                            <hr />
                            <h6 className="text-muted">Message complet</h6>
                            <div className="bg-light p-3 rounded">
                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                                    {selectedLog.message}
                                </pre>
                            </div>
                            {selectedLog.stack_trace && (
                                <>
                                    <hr />
                                    <h6 className="text-muted">Stack Trace</h6>
                                    <div className="bg-dark text-light p-3 rounded" style={{ fontSize: '0.8em' }}>
                                        <pre>{selectedLog.stack_trace}</pre>
                                    </div>
                                </>
                            )}
                            {selectedLog.extra_data && Object.keys(selectedLog.extra_data).length > 0 && (
                                <>
                                    <hr />
                                    <h6 className="text-muted">Données supplémentaires</h6>
                                    <div className="bg-light p-3 rounded">
                                        <pre style={{ fontSize: '0.8em' }}>
                                            {JSON.stringify(selectedLog.extra_data, null, 2)}
                                        </pre>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SuperAdminSystemLogs;
